import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalGames: number;
  todayRegistrations: number;
  paidUsers: number;
  freeUsers: number;
  averageElo: number;
  pageViews: number;
  uniqueVisitors: number;
  conversionRate: number;
}

export interface UserDetails {
  id: string;
  email: string;
  username: string;
  display_name: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  elo_rating: number;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  subscription_type: string;
  subscription_status: string;
  created_at: string;
  last_seen_at: string;
  avatar_url?: string;
  last_ip?: string;
  last_country?: string;
  last_city?: string;
  last_login?: string;
  login_count?: number;
  unique_countries?: number;
  is_suspicious_login?: boolean;
  // Enhanced analytics fields
  total_activities?: number;
  puzzles_attempted?: number;
  tutorials_accessed?: number;
  most_used_feature?: string;
  total_time_spent_hours?: number;
  last_activity?: string;
  activity_score?: number;
  last_used_date?: string;
}

export interface AnalyticsData {
  page_url: string;
  page_visits: number;
  unique_visitors: number;
  average_duration: number;
}

export const useAdmin = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats | null>(null);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('is_super_admin', { _user_id: user.id });
      
      if (error) {
        console.error('Admin check RPC error:', error);
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const { data: retryData, error: retryError } = await supabase.rpc('is_super_admin', { _user_id: user.id });
        if (retryError) {
          console.error('Admin check retry failed:', retryError);
          setIsAdmin(false);
        } else {
          setIsAdmin(retryData === true);
        }
      } else {
        setIsAdmin(data === true);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
    setLoading(false);
  };

  const fetchStats = async (): Promise<AdminStats | null> => {
    if (!isAdmin) return null;

    try {
      // Fetch user stats
      const { data: userStats } = await supabase
        .from('user_profiles')
        .select('id, elo_rating, games_played, created_at')
        .not('id', 'is', null);

      // Fetch subscription stats
      const { data: subscriptionStats } = await supabase
        .from('user_subscriptions')
        .select('subscription_type, status');

      // Fetch game stats
      const { data: gameStats, count: totalGames } = await supabase
        .from('game_sessions')
        .select('*', { count: 'exact' });

      // Fetch analytics stats
      const { data: analyticsStats } = await supabase
        .from('website_analytics')
        .select('user_id, session_id, created_at')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayRegistrations = userStats?.filter(
        u => new Date(u.created_at) >= today
      ).length || 0;

      const paidUsers = subscriptionStats?.filter(
        s => s.subscription_type !== 'free' && s.status === 'active'
      ).length || 0;

      const freeUsers = subscriptionStats?.filter(
        s => s.subscription_type === 'free'
      ).length || 0;

      const averageElo = userStats?.length ? 
        userStats.reduce((sum, u) => sum + u.elo_rating, 0) / userStats.length : 1200;

      const uniqueSessions = new Set(analyticsStats?.map(a => a.session_id)).size;
      const registeredUsersFromAnalytics = new Set(
        analyticsStats?.filter(a => a.user_id).map(a => a.user_id)
      ).size;

      const conversionRate = uniqueSessions > 0 ? 
        (registeredUsersFromAnalytics / uniqueSessions) * 100 : 0;

      const adminStats: AdminStats = {
        totalUsers: userStats?.length || 0,
        activeUsers: userStats?.filter(u => u.games_played > 0).length || 0,
        totalGames: totalGames || 0,
        todayRegistrations,
        paidUsers,
        freeUsers,
        averageElo: Math.round(averageElo),
        pageViews: analyticsStats?.length || 0,
        uniqueVisitors: uniqueSessions,
        conversionRate: Math.round(conversionRate * 100) / 100
      };

      setStats(adminStats);
      return adminStats;
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      return null;
    }
  };

  const fetchUsers = async (page = 1, limit = 50): Promise<UserDetails[]> => {
    if (!isAdmin) return [];

    try {
      // Use the new comprehensive analytics function for better performance
      // Note: Function is not yet in TypeScript types, so we use any
      const { data: analyticsData, error: analyticsError } = await supabase
        .rpc('get_comprehensive_user_analytics' as any);
      
      if (analyticsError) {
        console.error('Analytics fetch error:', analyticsError);
        // Fallback to basic user fetch if comprehensive analytics fails
        const { data: fallbackData } = await supabase
          .from('user_profiles')
          .select(`
            id,
            username,
            display_name,
            first_name,
            last_name,
            elo_rating,
            games_played,
            games_won,
            games_lost,
            games_drawn,
            created_at,
            avatar_url
          `)
          .range((page - 1) * limit, page * limit - 1)
          .order('created_at', { ascending: false });

        return fallbackData?.map((user: any) => ({
          id: user.id,
          email: 'Protected',
          username: user.username || 'N/A',
          display_name: user.display_name || 'N/A',
          first_name: user.first_name,
          last_name: user.last_name,
          full_name: user.display_name || 'N/A',
          elo_rating: user.elo_rating,
          games_played: user.games_played,
          games_won: user.games_won,
          games_lost: user.games_lost,
          games_drawn: user.games_drawn,
          subscription_type: 'free',
          subscription_status: 'active',
          created_at: user.created_at,
          last_seen_at: user.created_at,
          avatar_url: user.avatar_url,
          last_ip: 'Protected',
          last_country: 'Protected',
          last_city: 'Protected',
          last_login: null,
          login_count: 0,
          unique_countries: 0,
          is_suspicious_login: false,
          total_activities: 0,
          puzzles_attempted: 0,
          tutorials_accessed: 0,
          most_used_feature: 'None',
          total_time_spent_hours: 0,
          last_activity: user.created_at,
          activity_score: 0,
          last_used_date: user.created_at,
        })) || [];
      }

      // Type guard and apply pagination to the comprehensive data
      const userArray = Array.isArray(analyticsData) ? analyticsData : [];
      const total = userArray.length;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedUsers = userArray.slice(startIndex, endIndex);
      
      return paginatedUsers.map((user: any) => ({
        id: user.user_id,
        email: user.email || 'Protected',
        username: user.username || 'N/A',
        display_name: user.display_name || user.username || 'N/A',
        first_name: undefined,
        last_name: undefined,
        full_name: user.display_name || user.username || 'N/A',
        elo_rating: user.elo_rating || 1200,
        games_played: user.games_played || 0,
        games_won: 0, // Will be calculated from games_played data if needed
        games_lost: 0,
        games_drawn: 0,
        subscription_type: user.subscription_type || 'free',
        subscription_status: user.subscription_status || 'active',
        created_at: user.created_at,
        last_seen_at: user.last_activity || user.created_at,
        avatar_url: null,
        last_ip: 'Protected',
        last_country: user.current_country || 'Unknown',
        last_city: user.current_city || 'Unknown',
        last_login: user.last_login,
        login_count: user.login_count || 0,
        unique_countries: user.unique_countries || 0,
        is_suspicious_login: false,
        total_activities: user.total_activities || 0,
        puzzles_attempted: user.puzzles_attempted || 0,
        tutorials_accessed: user.tutorials_accessed || 0,
        most_used_feature: user.most_used_feature || 'None',
        total_time_spent_hours: Math.round((user.total_time_spent_seconds || 0) / 3600 * 10) / 10,
        last_activity: user.last_activity,
        activity_score: user.activity_score || 0,
        last_used_date: user.last_used_date || user.created_at,
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  };

  const fetchAnalytics = async (days = 30): Promise<AnalyticsData[]> => {
    if (!isAdmin) return [];

    try {
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      
      const { data } = await supabase
        .from('website_analytics')
        .select('page_url, session_id, duration_seconds')
        .gte('created_at', since.toISOString())
        .eq('event_type', 'page_view');

      const pageStats = new Map<string, {
        visits: number;
        sessions: Set<string>;
        totalDuration: number;
        durationCount: number;
      }>();

      data?.forEach(record => {
        const url = record.page_url;
        if (!pageStats.has(url)) {
          pageStats.set(url, {
            visits: 0,
            sessions: new Set(),
            totalDuration: 0,
            durationCount: 0
          });
        }
        const stats = pageStats.get(url)!;
        stats.visits++;
        stats.sessions.add(record.session_id);
        if (record.duration_seconds) {
          stats.totalDuration += record.duration_seconds;
          stats.durationCount++;
        }
      });

      return Array.from(pageStats.entries())
        .map(([page_url, stats]) => ({
          page_url,
          page_visits: stats.visits,
          unique_visitors: stats.sessions.size,
          average_duration: stats.durationCount > 0 ? 
            Math.round(stats.totalDuration / stats.durationCount) : 0
        }))
        .sort((a, b) => b.page_visits - a.page_visits);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return [];
    }
  };

  const resetUserPassword = async (userId: string, newPassword: string) => {
    if (!isAdmin) throw new Error('Unauthorized');

    try {
      console.log('Calling secure admin edge function for password reset');
      
      // Call the secure edge function instead of client-side admin operation
      const { data, error } = await supabase.functions.invoke('admin-password-reset', {
        body: {
          userId,
          newPassword
        }
      });

      if (error) {
        console.error('Password reset error:', error);
        throw new Error(`Failed to reset password: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'Password reset failed');
      }

      return { success: true };
    } catch (error) {
      console.error('Error resetting password:', error);
      return { success: false, error: (error as any)?.message || 'Unknown error' };
    }
  };

  return {
    isAdmin,
    loading,
    stats,
    fetchStats,
    fetchUsers,
    fetchAnalytics,
    resetUserPassword
  };
};